import { userHasPlatformAdminRole } from "@the-drop/types";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  fetchAdminDispensariesList,
  fetchDispensaryPublicSummary,
  fetchMeDispensaryMemberships,
  type DispensaryPublicSummary,
} from "../lib/dispensary-account-api";
import { StorefrontBannerCard } from "../components/StorefrontBannerCard";
import { fetchMe } from "../lib/fetch-me";
import { useDispensaryWorkspace } from "../workspace/dispensary-workspace-context";

function formatAddress(d: DispensaryPublicSummary): string {
  const line2 = d.addressLine2?.trim() ? `${d.addressLine2}, ` : "";
  return `${d.addressLine1}, ${line2}${d.city}, ${d.state} ${d.zip}`;
}

function roleLabels(roles: string[]): string {
  const map: Record<string, string> = {
    dispensary_dispatcher: "Dispatcher",
    dispensary_budtender: "Budtender",
  };
  return roles.map((r) => map[r] ?? r).join(" · ");
}

export default function DispensaryAccountScreen() {
  const { getToken } = useDispensaryWorkspace();

  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ["me", "account"],
    queryFn: () => fetchMe(getToken),
  });

  const me = meData?.user;
  const isPlatformAdmin = me ? userHasPlatformAdminRole(me) : false;

  const { data: membershipsRes, isLoading: memLoading } = useQuery({
    queryKey: ["dispensary", "me-memberships"],
    queryFn: () => fetchMeDispensaryMemberships(getToken),
    enabled: Boolean(me),
  });

  const { data: adminList, isLoading: adminLoading } = useQuery({
    queryKey: ["dispensary", "admin-list"],
    queryFn: () => fetchAdminDispensariesList(getToken),
    enabled: Boolean(me) && isPlatformAdmin,
  });

  const membershipIds = useMemo(
    () => membershipsRes?.memberships.map((m) => m.dispensaryId) ?? [],
    [membershipsRes],
  );

  const preferAdminList = Boolean(isPlatformAdmin && adminList && adminList.length > 0);

  const staffDetailsEnabled =
    Boolean(me) &&
    Boolean(membershipsRes) &&
    membershipIds.length > 0 &&
    !preferAdminList &&
    (!isPlatformAdmin || !adminLoading);

  const { data: staffDispensaryRows, isLoading: staffDispLoading } = useQuery({
    queryKey: ["dispensary", "account-details", membershipIds],
    queryFn: async () => {
      const rows: Array<{ dispensary: DispensaryPublicSummary; roles: string[] }> = [];
      for (const m of membershipsRes!.memberships) {
        const d = await fetchDispensaryPublicSummary(getToken, m.dispensaryId);
        if (d) rows.push({ dispensary: d, roles: m.roles });
      }
      return rows;
    },
    enabled: staffDetailsEnabled,
  });

  const displayRows = useMemo(() => {
    if (preferAdminList && adminList) {
      const memMap = new Map(membershipsRes?.memberships.map((m) => [m.dispensaryId, m.roles]) ?? []);
      return adminList.map((d) => ({
        dispensary: d,
        roles: memMap.get(d.id) ?? [],
        source: "admin" as const,
      }));
    }
    if (staffDispensaryRows) {
      return staffDispensaryRows.map((r) => ({ ...r, source: "membership" as const }));
    }
    return [];
  }, [preferAdminList, adminList, membershipsRes, staffDispensaryRows]);

  const loading =
    meLoading ||
    memLoading ||
    (isPlatformAdmin && adminLoading) ||
    (staffDetailsEnabled && staffDispLoading);

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#111111]">Account</h1>
        <p className="mt-1 text-sm text-[#555555]">
          Dispensaries and locations associated with your sign-in. The licensed premises on file is the primary
          location; additional retail locations will be listed here as they are configured for your organization.
        </p>
        {isPlatformAdmin ? (
          <p className="mt-3">
            <Link
              to="/workspace/admin/create-dispensary"
              className="inline-flex items-center rounded-lg bg-[#2F5D50] px-4 py-2 text-sm font-medium text-white hover:bg-[#264a40]"
            >
              Create new dispensary
            </Link>
          </p>
        ) : null}
      </div>

      <div className="rounded-2xl border border-[#EBEBEB] bg-white p-5 shadow-[0_1px_4px_rgba(0,0,0,.06)]">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-[#888888]">Your session</h2>
        {meLoading ? (
          <div className="mt-3 h-10 animate-pulse rounded bg-[#F0F0F0]" />
        ) : me ? (
          <dl className="mt-3 space-y-2 text-sm">
            <div className="flex flex-wrap gap-2">
              <dt className="text-[#888888]">Roles</dt>
              <dd className="font-mono text-[#111111]">{me.roles?.length ? me.roles.join(", ") : me.role}</dd>
            </div>
            <div className="flex flex-wrap gap-2">
              <dt className="text-[#888888]">Platform user id</dt>
              <dd className="break-all font-mono text-xs text-[#333333]">{me.platformUserId}</dd>
            </div>
          </dl>
        ) : (
          <p className="mt-2 text-sm text-[#991B1B]">Could not load profile.</p>
        )}
      </div>

      <div>
        <h2 className="mb-3 text-lg font-bold text-[#111111]">Your dispensaries</h2>
        {loading ? (
          <div className="space-y-3">
            {[0, 1].map((i) => (
              <div key={i} className="h-40 animate-pulse rounded-2xl border border-[#EBEBEB] bg-white" />
            ))}
          </div>
        ) : displayRows.length === 0 ? (
          <div className="rounded-2xl border border-[#EBEBEB] bg-white px-5 py-10 text-center text-sm text-[#888888] shadow-[0_1px_4px_rgba(0,0,0,.06)]">
            {isPlatformAdmin && adminList?.length === 0
              ? "No dispensaries found in the system."
              : "No dispensary associations found for this account. Ask an administrator to assign dispensary access."}
          </div>
        ) : (
          <ul className="space-y-4">
            {displayRows.map(({ dispensary: d, roles, source }) => (
              <li
                key={d.id}
                className="overflow-hidden rounded-2xl border border-[#EBEBEB] bg-white shadow-[0_1px_4px_rgba(0,0,0,.06)]"
              >
                <div className="border-b border-[#F0F0F0] px-5 py-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <h3 className="text-lg font-bold text-[#111111]">{d.dbaName?.trim() || d.legalName}</h3>
                      <p className="text-xs text-[#888888]">{d.legalName}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {source === "admin" ? (
                        <span className="rounded-lg bg-[#EBF5EB] px-2 py-1 text-2xs font-medium text-[#2D6B2D]">
                          Platform admin
                        </span>
                      ) : null}
                      {!d.isActive ? (
                        <span className="rounded-lg bg-[#FEE2E2] px-2 py-1 text-2xs font-medium text-[#991B1B]">
                          Inactive
                        </span>
                      ) : null}
                    </div>
                  </div>
                  {roles.length > 0 ? (
                    <p className="mt-2 text-sm text-[#555555]">
                      Your roles: <span className="font-medium text-[#111111]">{roleLabels(roles)}</span>
                    </p>
                  ) : source === "admin" ? (
                    <p className="mt-2 text-sm text-[#888888]">No dispensary-staff roles on this user for this shop.</p>
                  ) : null}
                </div>

                <div className="px-5 py-4">
                  <h4 className="text-2xs font-semibold uppercase tracking-wide text-[#AAAAAA]">Locations</h4>
                  <div className="mt-3 space-y-3">
                    <div className="rounded-xl border border-[#F0F0F0] bg-[#FAFAFA] px-4 py-3">
                      <p className="text-2xs font-semibold uppercase tracking-wide text-[#888888]">Primary (licensed)</p>
                      <p className="mt-1 text-sm text-[#111111]">{formatAddress(d)}</p>
                      <p className="mt-2 text-xs text-[#888888]">
                        MED {d.medLicenseType} · {d.medLicenseNumber} · expires {d.medLicenseExpiresAt}
                      </p>
                    </div>
                    {d.dutchieLocationId ? (
                      <div className="rounded-xl border border-[#F0F0F0] bg-white px-4 py-3">
                        <p className="text-2xs font-semibold uppercase tracking-wide text-[#888888]">
                          POS integration location
                        </p>
                        <p className="mt-1 font-mono text-sm text-[#333333]">{d.dutchieLocationId}</p>
                        <p className="mt-1 text-2xs text-[#AAAAAA]">
                          Adapter: {d.posAdapter ?? "—"} · Used for inventory sync with this storefront.
                        </p>
                      </div>
                    ) : null}
                    <p className="text-2xs leading-relaxed text-[#AAAAAA]">
                      Additional physical retail locations are not stored as separate rows yet; this screen shows the
                      licensed premises and any integrated POS location IDs. Contact your administrator to add
                      locations.
                    </p>
                  </div>
                </div>

                <StorefrontBannerCard
                  dispensaryId={d.id}
                  publicSlug={d.publicSlug}
                  bannerUpdatedAt={d.storefrontBannerUpdatedAt}
                  getToken={getToken}
                />

                <div className="border-t border-[#F0F0F0] bg-[#FAFAF8] px-5 py-3">
                  <dl className="grid gap-2 text-xs text-[#555555] sm:grid-cols-2">
                    <div>
                      <dt className="text-[#AAAAAA]">Shop contact</dt>
                      <dd>
                        {d.phone} · {d.email}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[#AAAAAA]">METRC</dt>
                      <dd className="font-mono">{d.metrcLicenseNumber}</dd>
                    </div>
                    <div>
                      <dt className="text-[#AAAAAA]">Metrc delivery reporting</dt>
                      <dd>
                        {d.metrcDeliveryReporter === "pos"
                          ? "Your point of sale reports deliveries to Metrc. Drop makes no Metrc calls for this location."
                          : "Drop reports deliveries to Metrc for this location."}
                      </dd>
                    </div>
                    {d.publicSlug ? (
                      <div className="sm:col-span-2">
                        <dt className="text-[#AAAAAA]">Public menu slug</dt>
                        <dd className="font-mono">{d.publicSlug}</dd>
                      </div>
                    ) : null}
                  </dl>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
